/**
 * Application Header Component
 * RiskMetrica Views - CFO Command Centre
 *
 * Fixed header with entity info and user controls
 */

import { useState } from 'react';
import { Bell, Moon, Sun, ChevronDown, Menu, X, Sparkles, User, Settings, LogOut } from 'lucide-react';
import { useSessionStore } from '../../stores/sessionStore';
import { useArbiterStore } from '../../stores/arbiterStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { cn } from '../../lib/utils';
import { showToast } from '../common';
import type { ScenarioType, Quarter } from '../../types/entities';

const SCENARIOS: ScenarioType[] = ['BASE', 'ADVERSE', 'SEVERE'];
const QUARTERS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

export function Header() {
  const { user, notifications, darkMode, toggleDarkMode, scenario, setScenario, quarter, setQuarter } = useSessionStore();
  const { togglePanel } = useArbiterStore();
  const { sidebarOpen, toggleSidebar } = useNavigationStore();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    setUserMenuOpen(false);
    showToast('Session ended — redirecting to login');
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 bg-slate-900 text-white border-b border-slate-700 z-40"
      style={{ height: 'var(--header-height, 64px)' }}
    >
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left: Entity & Application Info */}
        <div className="flex items-center gap-4">
          {/* Sidebar Toggle */}
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className="flex items-center gap-3">
            <img src="/riskmetrica-logo.png" alt="RiskMetrica" className="h-8" />
            <div className="h-6 w-px bg-slate-600" />
            <div className="flex items-center gap-2 text-sm">
              <span className="text-lg font-semibold text-white">RiskMetrica</span>
              <span className="text-slate-500">|</span>
              <span className="text-slate-400">CFO</span>
              <span className="text-slate-500">|</span>
              <span className="font-semibold text-white">CFO Command Centre</span>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">UK Bank plc</span>
          </div>
        </div>

        {/* Center: Period & Scenario Selectors */}
        <div className="flex items-center gap-4">
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value as Quarter)}
            className="px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {QUARTERS.map((q) => (
              <option key={q} value={q}>{q} 2024</option>
            ))}
          </select>

          <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
            {SCENARIOS.map((s) => (
              <button
                key={s}
                onClick={() => setScenario(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  scenario === s
                    ? 'bg-slate-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-4">
          {/* Theme Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? (
              <Sun className="w-5 h-5 text-amber-400" />
            ) : (
              <Moon className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors relative"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-400" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>

            {notificationsOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setNotificationsOpen(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-xl z-20 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-semibold text-slate-800 dark:text-white">
                      Notifications
                    </h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-0 cursor-pointer"
                        >
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            {notification.title}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            {notification.message}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                            {new Date(notification.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">
                        No new notifications
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Arbiter AI Assistant */}
          <button
            onClick={togglePanel}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all',
              'bg-gradient-to-r from-blue-600 to-purple-600 text-white',
              'hover:from-blue-700 hover:to-purple-700',
              'shadow-sm hover:shadow-md'
            )}
          >
            <Sparkles className="w-4 h-4" />
            Arbiter
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                <span className="text-xs font-semibold text-slate-300">
                  {user.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{user.name}</div>
                <div className="text-xs text-slate-400">{user.role}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl z-20 overflow-hidden">
                  <div className="p-3 border-b border-slate-100 dark:border-slate-700">
                    <div className="text-sm font-medium text-slate-800 dark:text-white">
                      {user.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {user.email}
                    </div>
                  </div>
                  <div className="py-1">
                    <button onClick={() => { setUserMenuOpen(false); showToast('Profile management is available through your SSO provider'); }} className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Profile
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); showToast('Settings panel — coming in next release'); }} className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                  </div>
                  <div className="border-t border-slate-100 dark:border-slate-700 py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
